/**
 * LessonPlayer.jsx — individual reader module page.
 *
 * Renders a full training module:
 *   • Overview
 *   • 3–4 sub-lessons (bulleted teaching content)
 *   • 2 exercises (reference)
 *   • Reading challenge brief (reference)
 *   • 5-question quiz → pass ≥ 60% to complete & earn XP/coins
 *
 * Phases: read → quiz → result.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BookOpen, CheckCircle2, XCircle, ArrowRight, Loader2, RotateCcw,
  Target, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LessonPlayer = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [phase, setPhase] = useState('read');
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/reader/courses/lesson/${lessonId}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          toast.error(d.detail || 'Module unavailable');
          navigate('/reading-course');
          return;
        }
        const d = await r.json();
        setMod(d);
        setAnswers(new Array(d.questions.length).fill(null));
      } catch {
        toast.error('Could not load module');
      }
    })();
  }, [lessonId, navigate]);

  const selectAnswer = (qIdx, oIdx) => {
    const next = answers.slice();
    next[qIdx] = oIdx;
    setAnswers(next);
  };

  const submit = async () => {
    if (answers.some((a) => a === null)) {
      toast.info('Answer every question first.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/reader/courses/lesson/${lessonId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setResult(data);
      setPhase('result');
      if (data.passed && !data.already_completed) {
        toast.success(`+${data.xp_awarded} XP · +${data.coins_awarded} coins`);
      }
    } catch (e) {
      toast.error(e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setPhase('read');
    setAnswers(new Array(mod.questions.length).fill(null));
    setResult(null);
  };

  if (!mod) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-300">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6" data-testid="lesson-player-page">
      <button
        type="button"
        onClick={() => navigate('/reading-course')}
        className="text-xs text-gray-400 hover:text-white mb-4"
        data-testid="lesson-back"
      >
        ← back to courses
      </button>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-400" /> {mod.title}
            </CardTitle>
            <span className="text-[11px] uppercase text-gray-400 tracking-wider">
              {mod.path} · +{mod.xp} XP · +{mod.coins} coins
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {phase === 'read' && (
            <>
              <section
                className="p-4 bg-slate-900/60 rounded-lg border border-slate-700/60"
                data-testid="lesson-passage"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-300 mb-2">
                  <BookOpen className="w-3.5 h-3.5" /> Overview
                </div>
                <p className="text-gray-200 leading-relaxed text-[15px]">{mod.overview}</p>
              </section>

              <section className="space-y-3">
                {mod.lessons.map((lsn) => (
                  <div
                    key={lsn.id}
                    className="p-4 rounded-lg bg-slate-900/40 border border-slate-700/50"
                    data-testid={`sublesson-${lsn.id}`}
                  >
                    <div className="text-[11px] uppercase tracking-widest text-indigo-300 mb-1">
                      Lesson {lsn.id}
                    </div>
                    <h3 className="text-white font-semibold mb-1.5">{lsn.title}</h3>
                    <pre className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-sans m-0">{lsn.body}</pre>
                  </div>
                ))}
              </section>

              {mod.exercises?.length > 0 && (
                <section className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-300 mb-2">
                    <Target className="w-3.5 h-3.5" /> Exercises
                  </div>
                  <ul className="space-y-3">
                    {mod.exercises.map((ex, i) => (
                      <li key={i}>
                        <div className="text-sm font-semibold text-white">
                          {ex.title} <span className="text-[11px] text-emerald-300 font-normal">({ex.words})</span>
                        </div>
                        <p className="text-xs text-gray-300 mt-0.5 leading-relaxed">{ex.prompt}</p>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {mod.project && (
                <section className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300 mb-2">
                    <Award className="w-3.5 h-3.5" /> Reading challenge
                  </div>
                  <div className="text-sm font-semibold text-white">
                    {mod.project.title} <span className="text-[11px] text-amber-300 font-normal">({mod.project.words})</span>
                  </div>
                  <p className="text-xs text-gray-300 mt-1 leading-relaxed">{mod.project.brief}</p>
                </section>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => setPhase('quiz')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="lesson-take-quiz"
                >
                  <ArrowRight className="w-4 h-4 mr-2" /> Take the {mod.questions.length}-question quiz
                </Button>
              </div>
            </>
          )}

          {phase === 'quiz' && (
            <>
              <div className="text-xs text-gray-400 mb-2">
                Pass mark: {mod.passPct || 60}%. Rewards mint once per module — replays are free practice.
              </div>
              <ul className="space-y-4" data-testid="lesson-quiz">
                {mod.questions.map((q, qi) => (
                  <li key={qi} className="p-3 bg-slate-900/40 rounded-lg border border-slate-700">
                    <p className="text-sm font-semibold text-white mb-2">
                      {qi + 1}. {q.q}
                    </p>
                    <div className="grid gap-2">
                      {q.options.map((opt, oi) => {
                        const active = answers[qi] === oi;
                        return (
                          <button
                            key={oi}
                            type="button"
                            onClick={() => selectAnswer(qi, oi)}
                            data-testid={`lesson-q${qi}-opt${oi}`}
                            className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                              active
                                ? 'bg-indigo-600/20 border-indigo-400 text-white'
                                : 'bg-slate-800/60 border-slate-700 text-gray-200 hover:border-slate-500'
                            }`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ul>
              <Button
                onClick={submit}
                disabled={submitting}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                data-testid="lesson-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit answers
              </Button>
            </>
          )}

          {phase === 'result' && result && (
            <div className="space-y-4" data-testid="lesson-result">
              <div
                className={`p-5 rounded-xl border ${
                  result.passed ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {result.passed ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                  ) : (
                    <XCircle className="w-5 h-5 text-rose-300" />
                  )}
                  <h3 className="text-lg font-bold text-white">
                    {result.passed ? (result.already_completed ? 'Already completed' : 'Module complete!') : 'Keep going'}
                  </h3>
                </div>
                <p className="text-sm text-gray-200">
                  Score: <span className="font-bold">{result.score_pct}%</span> · {result.correct}/{result.total} correct
                </p>
                {result.passed && !result.already_completed && (
                  <p className="text-sm text-emerald-300 font-semibold mt-1">
                    +{result.xp_awarded} XP · +{result.coins_awarded} coins
                  </p>
                )}
                {!result.passed && (
                  <p className="text-xs text-rose-200 mt-2">
                    You need {mod.passPct || 60}% or higher to complete this module. Re-read and try again.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={retry}
                  variant="outline"
                  className="border-slate-600 text-gray-200"
                  data-testid="lesson-retry"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Retry
                </Button>
                <Button
                  onClick={() => navigate('/reading-course')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="lesson-back-to-courses"
                >
                  Back to courses
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default LessonPlayer;
